import { Clock, Globe, Mail, MapPin, Phone } from "lucide-react";
import { useSiteData } from "@/context/SiteDataContext";
import { EnquiryForm } from "@/components/site/EnquiryForm";
import { WhatsAppIcon } from "@/components/site/WhatsAppIcon";
import { waLink } from "@/lib/api";
import { IMG } from "@/lib/constants";
import { PageHero } from "@/components/site/PageHero";
import { FullLogo } from "@/components/site/Logo";
import { Seo, breadcrumb } from "@/components/site/Seo";

const Item = ({ icon: Icon, label, value, href, testId }) => (
  <div className="flex gap-4 rounded-2xl border border-slate-200 bg-white p-6" data-testid={testId}>
    <span className="h-11 w-11 shrink-0 rounded-xl bg-sky-50 text-ocean grid place-items-center"><Icon className="h-5 w-5" /></span>
    <div className="min-w-0">
      <p className="text-xs uppercase tracking-[0.2em] font-semibold text-cyan-700">{label}</p>
      {href ? <a href={href} target="_blank" rel="noreferrer" className="mt-1 block font-semibold text-navy hover:text-ocean break-words">{value}</a>
        : <p className="mt-1 font-semibold text-navy whitespace-pre-line break-words">{value}</p>}
    </div>
  </div>
);

export default function Contact() {
  const { settings } = useSiteData();
  const hasOffice = settings.phone || settings.email || settings.address || settings.office_hours;
  const site = settings.website?.startsWith("http") ? settings.website : `https://${settings.website}`;

  return (
    <div data-testid="contact-page">
      <Seo title="Contact Ocean Wings Travel | Andaman Tour Packages Enquiry"
        description="Contact Ocean Wings Travel to plan your Andaman trip. WhatsApp or send an enquiry for tour packages, ferry tickets and hotels in Port Blair, Havelock and Neil Island."
        path="/contact" jsonLd={[breadcrumb([["Home", "/"], ["Contact", "/contact"]])]} />
      <PageHero img={IMG.aerial} alt="Aerial view of Andaman coastline" eyebrow={`Contact ${settings.company_name}`} title="Let's plan your island escape" testId="contact-hero"
        subtitle="Packages, ferry tickets or hotels: WhatsApp is the fastest way to reach our Andaman team.">
        <a href={waLink(settings.whatsapp, "Hi Ocean Wings Travel, I'd like to plan an Andaman trip.")} target="_blank" rel="noreferrer" data-testid="contact-hero-whatsapp"
          className="inline-flex items-center gap-2 h-12 px-7 rounded-full bg-emerald-500 text-white font-semibold hover:bg-emerald-600 transition-colors"><WhatsAppIcon className="h-5 w-5" /> Enquire on WhatsApp</a>
      </PageHero>
      <section className="container-x py-14 grid gap-12 lg:grid-cols-12">
        <div className="lg:col-span-5 space-y-4">
          <div className="rounded-2xl border border-slate-200 bg-white p-6 flex items-center gap-5" data-testid="contact-brand-card">
            <FullLogo className="w-24 sm:w-28" />
            <div><p className="font-display text-2xl text-navy font-semibold">{settings.company_name}</p><p className="text-sm text-slate-500">Andaman &amp; Nicobar Islands travel specialists</p></div>
          </div>
          <h2 className="font-display text-3xl text-navy font-semibold">Company &amp; office details</h2>
          <Item icon={WhatsAppIcon} label="WhatsApp" value={settings.whatsapp} href={waLink(settings.whatsapp, "Hi Ocean Wings Travel!")} testId="contact-whatsapp" />
          <Item icon={Globe} label="Website" value={settings.website} href={site} testId="contact-website" />
          {settings.phone && <Item icon={Phone} label="Phone" value={settings.phone} href={`tel:${settings.phone}`} testId="contact-phone" />}
          {settings.email && <Item icon={Mail} label="Email" value={settings.email} href={`mailto:${settings.email}`} testId="contact-email" />}
          {settings.address && <Item icon={MapPin} label="Office address" value={settings.address} testId="contact-address" />}
          {settings.office_hours && <Item icon={Clock} label="Office hours" value={settings.office_hours} testId="contact-hours" />}
          {!hasOffice && (
            <p className="text-sm text-slate-500 rounded-2xl bg-mist p-5" data-testid="contact-office-note">
              Our office address and phone line will be listed here soon. In the meantime, WhatsApp is the fastest way to reach us.
            </p>
          )}
          {settings.address && (
            <iframe title="Office location" data-testid="contact-map" className="w-full h-64 rounded-2xl border border-slate-200" loading="lazy"
              src={`https://www.google.com/maps?q=${encodeURIComponent(settings.address)}&output=embed`} />
          )}
        </div>
        <div className="lg:col-span-7 rounded-3xl border border-slate-200 p-6 sm:p-8">
          <h2 className="font-display text-3xl text-navy font-semibold">Send us an enquiry</h2>
          <p className="mt-1 mb-6 text-sm text-slate-600">We usually reply with a detailed quotation the same day.</p>
          <EnquiryForm idPrefix="contact-enquiry" />
        </div>
      </section>
    </div>
  );
}
